import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import type { User } from 'firebase/auth';
import { PORTAL_PATHS } from '@x9/billing';
import { interceptLinkClicks } from '@x9/web-work/navigation';
import { parseRoute } from '@x9/web-work/routing';
import { ensureSessionReady } from '@x9/web-work/services/auth';
import App from './App';
import PortalLoader from './components/PortalLoader';
import { useAuthUser } from './hooks/useAuthUser';
import { useUserRecovery } from './hooks/useUserRecovery';
import { isPortalPath } from './routing';

const WorkRoutes = lazy(() =>
  import('@x9/web-work/App').then((m) => ({ default: m.WorkRoutes })),
);

function isPhoneAccount(user: User | null): boolean {
  return !!user && !user.isAnonymous && !!user.phoneNumber;
}

/**
 * One origin, two products: billing paths stay in the portal App;
 * everything else is the shared work tree from apps/web-x9.
 *
 * Reading surfaces (`/`, lists, routines, …) are the account door.
 * Nobody reaches them from a printed QR or a WhatsApp execute link —
 * those stay on `/q/` `/t/` `/r/` `/f/`. An unsigned visitor here
 * already wants the web account, so every width gets AuthExperience
 * (SMS first, then Terms). Capture / execute / follow are unchanged.
 */
export default function Root() {
  const [href, setHref] = useState(
    () => window.location.pathname + window.location.search,
  );
  const { user, loading } = useAuthUser();
  const { recovery, recoveryReady } = useUserRecovery(!!user);

  /*
   * The work tree resolves its own session (profile reload + fresh token)
   * before it will paint. Waiting for the gate to admit us first made those
   * two round trips serial, and the reader watched a loader through both.
   *
   * Starting it here overlaps it with the `users/{uid}` read the gate is
   * already waiting on: `ensureSessionReady` memoises one bootstrap per tab,
   * so `SessionProvider` finds it settled and the board paints as soon as the
   * gate opens. Strictly for accounts that already carry a phone — the
   * bootstrap signs in anonymously otherwise, which is not a session the
   * portal should ever mint.
   */
  useEffect(() => {
    if (!isPhoneAccount(user)) return;
    void ensureSessionReady().catch(() => undefined);
  }, [user]);

  useEffect(() => {
    const sync = () => setHref(window.location.pathname + window.location.search);
    window.addEventListener('popstate', sync);
    const stop = interceptLinkClicks();
    return () => {
      window.removeEventListener('popstate', sync);
      stop();
    };
  }, []);

  const url = new URL(href, window.location.origin);
  const workRoute = parseRoute(url.pathname, url.search);
  const forcedSignInRoute = useMemo(() => {
    const next = `${url.pathname}${url.search}` || PORTAL_PATHS.HOME;
    return {
      name: 'signin' as const,
      nextPath: next.startsWith('/') ? next : PORTAL_PATHS.HOME,
    };
  }, [url.pathname, url.search]);
  const isReadingSurface = [
    'home',
    'homeList',
    'homeLens',
    'homeBucket',
    'homeRoutine',
    'homeRoutines',
    'homeContact',
    'homeBuckets',
    'homeContacts',
    'homeFind',
    'homeActivity',
    'homeSettings',
    'designLab',
  ].includes(workRoute.kind);
  const canUseWork =
    isPhoneAccount(user)
    && recoveryReady
    && recovery.termsAccepted;

  if (isPortalPath(url.pathname)) {
    return <App />;
  }

  if (isReadingSurface && !canUseWork) {
    if (loading && !user) {
      return <PortalLoader />;
    }
    return <App forcedRoute={forcedSignInRoute} />;
  }

  return (
    <Suspense fallback={<PortalLoader />}>
      <WorkRoutes route={workRoute} />
    </Suspense>
  );
}
